import * as _ from 'lodash';
import { Component } from 'react';
import { Button, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import type { ReactNode } from 'react';

import Icon, { icons, IconText } from '../generic/Icon';

type ModalWizardState = {
  step: number;
};

type ModalWizardProps = {
  title?: string;
  initStep?: number;
  canNextStep: (currentStep: number) => boolean | string;
  canPrevStep: (currentStep: number) => boolean | string;
  showStep: (step: number) => boolean; // Allows callers to skip steps
  nextStepText: string;
  finishText?: ReactNode;
  onFinish: () => boolean | void;
  stepNames: string[];
  show?: boolean;
  onHide: () => void;
};

class ModalWizard extends Component<ModalWizardProps, ModalWizardState> {
  static defaultProps = {
    canNextStep() {
      return true;
    },
    canPrevStep() {
      return true;
    },
    showStep() {
      return true;
    },
    nextStepText: 'Next Step',
    finishText: 'Finish',
    onFinish() {},
    stepNames: [],
  } satisfies Partial<ModalWizardProps>;

  constructor(props: ModalWizardProps) {
    super(props);
    this.state = {
      step: props.initStep || 1,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps: ModalWizardProps) {
    if (nextProps.show && !this.props.show) {
      this.setState({ step: this.props.initStep || 1 });
    }
  }

  shouldComponentUpdate(nextProps: ModalWizardProps) {
    return Boolean(this.props.show) || this.props.show !== nextProps.show;
  }

  prevStep = () => {
    this.setState((prev) => (prev.step > 1 ? { step: prev.step - 1 } : null));
  };

  nextStep = () => {
    const children = this.getFilteredChildren();
    if (this.state.step >= children.length) {
      if (this.props.onFinish() === false) {
        return;
      }
      this.props.onHide();
      return;
    }
    this.setState((prev) => ({ step: prev.step + 1 }));
  };

  getFilteredChildren() {
    const children = _.castArray(this.props.children);
    return children.filter((_child, i) => this.props.showStep(i + 1));
  }

  getFilteredStepNames() {
    return this.props.stepNames.filter((_name, i) => this.props.showStep(i + 1));
  }

  /**
   * Wires keyboard navigation onto the wizard modal: ArrowLeft moves to the
   * previous step, ArrowRight advances to the next step (or finishes on the
   * last step). Bound to the Modal via the `onKeyDown` prop.
   *
   * The `ev` parameter is structurally typed (`{ key: string }`) rather than
   * `KeyboardEvent`/`KeyboardEventHandler<Modal>` to sidestep a contravariance
   * mismatch with react-bootstrap v0's legacy handler signature.
   */
  onKeyDown = (ev: { key: string }) => {
    if (ev.key === 'ArrowLeft') {
      this.prevStep();
    } else if (ev.key === 'ArrowRight') {
      this.nextStep();
    }
  };

  render() {
    const nextStepValidation = this.props.canNextStep(this.state.step);
    const prevStepValidation = this.props.canPrevStep(this.state.step);
    const canNextStep = nextStepValidation === true;
    const canPrevStep = this.state.step > 1 && prevStepValidation === true;
    const children = this.getFilteredChildren();
    const stepNames = this.getFilteredStepNames();

    const navItems = children.map((_child, i) => (
      <li className={this.state.step > i ? 'active' : ''} key={`wiz-nav-step${i + 1}`}>
        <a>{stepNames[i] ? `${i + 1}. ${stepNames[i]}` : `Step ${i + 1}`}</a>
      </li>
    ));
    return (
      <Modal {...this.props} className="wizard" onKeyDown={this.onKeyDown}>
        <Modal.Header closeButton>
          <Modal.Title>{this.props.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="container-fluid">
          {navItems.length > 1 ? (
            <div className="col-md-3 wizard-nav">
              <ul className="nav">{navItems}</ul>
            </div>
          ) : (
            ''
          )}
          <div className={`${navItems.length > 1 ? 'col-md-9' : ''} wizard-content`}>
            {this.props.show ? children[this.state.step - 1] : ''} {/* TODO: delay the child unmounts */}
          </div>
        </Modal.Body>
        <Modal.Footer>
          {children.length > 1 ? (
            <Button onClick={this.prevStep} className="pull-left" disabled={!canPrevStep}>
              <IconText
                text="Prev Step"
                icon={icons.arrowLeft}
              />
            </Button>
          ) : (
            ''
          )}
          {_.isString(prevStepValidation) ? (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="prevValidatorTooltip">{prevStepValidation}</Tooltip>}>
              <a className="pull-left" style={{ padding: '7px' }}>
                <Icon icon={icons.questionCircle} />
              </a>
            </OverlayTrigger>
          ) : (
            ''
          )}
          <Button
            onClick={this.nextStep}
            className="pull-right wizard-next-step"
            disabled={!canNextStep}
            bsStyle={canNextStep ? 'primary' : 'default'}>
            {this.state.step < children.length ? (
              <span>
                <IconText
                  icon={icons.arrowRight}
                  text={this.props.nextStepText}
                  alignIcon="right"
                />
              </span>
            ) : (
              this.props.finishText
            )}
          </Button>
          {_.isString(nextStepValidation) ? (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="nextValidatorTooltip">{nextStepValidation}</Tooltip>}>
              <a className="pull-right" style={{ padding: '7px' }}>
                <Icon icon={icons.questionCircle} />
              </a>
            </OverlayTrigger>
          ) : (
            ''
          )}
          {children.length > 1 ? (
            <p className="text-center subtitle nomargin-v">
              Step {this.state.step}/{children.length}
            </p>
          ) : (
            ''
          )}
        </Modal.Footer>
      </Modal>
    );
  }
}

export default ModalWizard;
